<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Sign In</h1>
            <p class="text-blue-100">Access your account</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1 max-w-md mx-auto">
        <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
          <div class="p-6">
            <form class="space-y-5" @submit.prevent="login">
              <!-- Email Input -->
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1 text-left">Email</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">mail</span>
                  </div>
                  <input
                    id="email"
                    v-model="email"
                    type="email"
                    required
                    class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 placeholder-gray-400 transition duration-200"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <!-- Password Input -->
              <div>
                <label for="password" class="block text-sm font-medium text-gray-700 mb-1 text-left">Password</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">lock</span>
                  </div>
                  <input
                    id="password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    required
                    class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 placeholder-gray-400 transition duration-200"
                    placeholder="••••••••"
                    @keyup.enter="login"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button 
                      type="button" 
                      @click="showPassword = !showPassword"
                      class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <span class="material-symbols-outlined text-xl">
                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <input id="remember-me" name="remember-me" type="checkbox" class="h-4 w-4 text-blue-900 focus:ring-blue-900 border-gray-300 rounded" />
                  <label for="remember-me" class="ml-2 block text-sm text-gray-700">Remember me</label>
                </div>

                <div class="text-sm">
                  <router-link to="/forgot-password" class="font-medium text-blue-900 hover:text-yellow-500 transition-colors">
                    Forgot password?
                  </router-link>
                </div>
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                class="w-full flex items-center justify-center py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 transition disabled:opacity-60"
              >
                Sign In
                <span class="material-symbols-outlined ml-2">login</span>
              </button>

              <div class="relative h-px bg-gray-100 my-6">
                <span class="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-3 text-xs text-gray-400">OR</span>
              </div>

              <router-link to="/register" class="w-full flex items-center justify-center py-3 px-4 rounded-lg font-semibold text-blue-900 border border-blue-900 bg-white hover:bg-blue-50 transition">
                Create an account
                <span class="material-symbols-outlined ml-2">person_add</span>
              </router-link>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import { loginUser } from '@/composables/useAuth';

export default {
  components: {
    AppHeader
  },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      loading: false
    };
  },
  methods: {
    async login() {
      this.loading = true;
      
      try {
        const result = await loginUser(this.email, this.password);
        console.log("Login result:", result);
        
        this.$store.dispatch('showAlert', {
          message: "Login successful!",
          type: "success"
        });
        
        this.$router.replace('/profile');
        console.log("Redirecting to profile page after login");
      } catch (error) {
        console.error("Login error:", error);
        // Show user-friendly error messages using showAlert
        let message = "An error occurred during login.";
        if (
          error.code === 'auth/user-not-found' ||
          error.code === 'auth/wrong-password' ||
          error.code === 'auth/invalid-credential'
        ) {
          message = "Invalid email or password. Please try again.";
        } else if (error.code === 'auth/too-many-requests') {
          message = "Too many failed login attempts. Please try again later or reset your password.";
        } else if (error.code === 'auth/user-disabled') {
          message = "This account has been disabled. Please contact support.";
        } else if (error.code === 'auth/network-request-failed') {
          message = "Network error. Please check your internet connection and try again.";
        } else if (error.message) {
          message = error.message;
        }
        this.$store.dispatch('showAlert', {
          message,
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<!-- No custom CSS needed, all styling is handled by Tailwind classes -->